import cx from "classnames";
import React, { useMemo } from "react";
import { Select } from "components/Select/Select";
import { useTranslate } from "hooks";
import type { Accessory, AccessoryGroup } from "interfaces/schemas/accessories";
import { SelectedAccessoryDetails } from "./SelectedAccessoryDetails";

interface IProductAccessoriesModalPartProps {
  onSelectionChange: (value: string) => void;
  accessoryGroup: AccessoryGroup;
  selectedAccessories: Accessory[];
}

type SelectedAccessoriesMap = Record<string, Accessory>;

export const ProductAccessoriesModalPart: React.FC<
  IProductAccessoriesModalPartProps
> = ({ onSelectionChange, accessoryGroup, selectedAccessories }) => {
  const translate = useTranslate();

  const selectedAccessoriesMap = useMemo(
    () =>
      selectedAccessories.reduce((accessory, item) => {
        accessory[item.accessoryGroupId] = item;
        return accessory;
      }, {} as SelectedAccessoriesMap),
    [selectedAccessories],
  );

  const selectedAccessory = selectedAccessoriesMap[accessoryGroup.id];

  const withoutOption = useMemo(
    () =>
      accessoryGroup.accessories.find((accessory) => accessory.seriesId === 0),
    [accessoryGroup.accessories],
  );

  const selectedValue = useMemo(() => {
    if (selectedAccessory?.currentPresentation) {
      return selectedAccessory.currentPresentation;
    }

    return (
      withoutOption?.currentPresentation ||
      translate("PRODUCT_CONFIGURATOR_WITHOUT")
    );
  }, [selectedAccessory, withoutOption, translate]);

  const accessoryOptions = accessoryGroup.accessories.map((accessory) => ({
    label: accessory.currentPresentation,
    value: accessory.currentPresentation,
  }));

  const handleChange = (
    selectedOption: { label: string; value: string } | null,
  ) => {
    onSelectionChange(selectedOption?.value || "");
  };

  return (
    <div
      className={cx(
        "d-flex flex-column flex-grow-1 flex-shrink-2 px-4 py-3 bg-white border rounded-2",
      )}
    >
      <h3 className="text-capitalize">{accessoryGroup.name}</h3>

      <Select
        options={accessoryOptions}
        value={accessoryOptions.find((opt) => opt.value === selectedValue)}
        onChange={handleChange}
        name="accessorySelect"
        testId={`accessory-select-${accessoryGroup.id}`}
        className="font-md w-100 text-capitalize"
        menuPlacement="auto"
        menuPosition="fixed"
      />

      <SelectedAccessoryDetails selectedAccessory={selectedAccessory} />
    </div>
  );
};
